import type { BaseComponent } from "../install/types.ts";
import type { Directory } from "../types/policy/types.ts";

/** Software maturity level
 *
 * - "preview": Early stage, may have limited features or support or there might be
 *  limited availability in customer environments.
 * - "beta": Feature complete but may have minor issues
 * - "ga": Generally available, production-ready
 * - "deprecated": No longer officially supported by P0
 */
export type Maturity = "beta" | "deprecated" | "ga" | "preview";

/** Maturity level of an installation component
 *
 * Extends {@link Maturity} with "dev": a development artifact that is not yet
 * ready for preview, and is only visible in devLike environments.
 */
export type ComponentMaturity = Maturity | "dev";

export type IntegrationStatus = "configure" | "error" | "installed" | "stage";

export type ResourceConfigUse =
  /** Read IAM access logs */
  | "access_logs"
  /** Any installation */
  | "any"
  /** Authenticate / authorize users */
  | "auth"
  // Deprecated
  | "function-caller"
  /** Read IAM state and analyzer data */
  | "iam_read"
  /** Set IAM configuration */
  | "iam_write"
  /** Identifies principals */
  | "identity"
  /** Provide network access to self-hosted resources */
  | "network_access"
  /** Perform key rotation.
   *  Demo only, behind feature flag assessment-manage */
  | "rotate_keys";

export type IntegrationState<Status extends string = string> = {
  state: IntegrationStatus | Status;
  error?: string;
};

export type ComponentConfig<S extends string, K extends string> = {
  [KK in K]?: { items: IntegrationState<S>[] } | undefined;
};

export type IntegrationConfig<
  Status extends string = string,
  Components extends object = object,
> = {
  [C in keyof Components]: {
    items: (Components[C] & IntegrationState<Status>)[];
  };
} & {
  state?: Status;
  error?: string;
  sandbox?: boolean /** In a sandbox environment, we do not expect users to exist in the target system */;
};

/** Configs generated from an InstallSpec follow this pattern */
export type NewConfig = Record<string, Record<string, BaseComponent>>;
export const isNewConfig = (config: OldOrNewConfig): config is NewConfig =>
  typeof config.state !== "string";

export type OldOrNewConfig = IntegrationConfig | NewConfig;

export enum SharedGCloudErrorMessage {
  ALREADY_HAS_ROLE = "Requestor already has access to permissions",
}

export type DirectoryToIntegrationConfig = Record<Directory, IntegrationConfig>;
